import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function RecipeForm({ products, stockItems, existingRecipe, onClose, authAxios }) {
  const [productId, setProductId] = useState(existingRecipe?.product_id || "");
  const [ingredients, setIngredients] = useState(
    existingRecipe?.ingredients?.map(i => ({ stock_id: i.stock_id, quantity: i.quantity.toString() })) ||
    [{ stock_id: "", quantity: "" }]
  );
  const [loading, setLoading] = useState(false);

  const addRow = () => setIngredients([...ingredients, { stock_id: "", quantity: "" }]);
  const removeRow = (i) => setIngredients(ingredients.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const updated = [...ingredients];
    updated[i] = { ...updated[i], [field]: value };
    setIngredients(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = ingredients.filter(i => i.stock_id && parseFloat(i.quantity) > 0);
    if (!productId || valid.length === 0) {
      toast.error("Selecciona producto e ingredientes");
      return;
    }
    setLoading(true);
    try {
      await authAxios.post("/stock/recipes", {
        product_id: productId,
        ingredients: valid.map(i => ({ stock_id: i.stock_id, quantity: parseFloat(i.quantity) })),
      });
      toast.success("Ficha tecnica guardada");
      onClose();
    } catch {
      toast.error("Error");
    }
    setLoading(false);
  };

  const availableProducts = products.filter(p => !existingRecipe || p.id === existingRecipe.product_id);

  return (
    <>
      <DialogHeader><DialogTitle>Ficha Tecnica</DialogTitle></DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
        <div className="space-y-2">
          <Label>Producto</Label>
          <Select value={productId} onValueChange={setProductId} disabled={!!existingRecipe}>
            <SelectTrigger data-testid="recipe-product">
              <SelectValue placeholder="Seleccionar producto..." />
            </SelectTrigger>
            <SelectContent>
              {availableProducts.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name} ({p.price}EUR)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Label>Ingredientes</Label>
        {ingredients.map((ing, i) => (
          <div key={i} className="flex gap-2 items-end">
            <div className="flex-1">
              <Select value={ing.stock_id} onValueChange={v => updateRow(i, "stock_id", v)}>
                <SelectTrigger><SelectValue placeholder="Ingrediente..." /></SelectTrigger>
                <SelectContent>
                  {stockItems.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name} ({s.unit})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-24">
              <Input
                type="number"
                step="0.001"
                placeholder="Qty"
                value={ing.quantity}
                onChange={e => updateRow(i, "quantity", e.target.value)}
              />
            </div>
            {ingredients.length > 1 && (
              <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(i)}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addRow} className="gap-1">
          <Plus className="w-3 h-3" />Agregar
        </Button>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={loading} data-testid="recipe-save">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
